'use client';

import type { ReactNode } from 'react';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { DesktopShell } from '@/shared/ui';
import { AppSidebar } from './AppSidebar';
import {
  DesktopLayoutProvider,
  useOptionalDesktopLayout,
} from '../context/DesktopLayoutContext';
import { UnifiedRenderPanel } from '@/features/design/render';

interface ProjectDesktopLayoutProps {
  children: ReactNode;
  projectId: string;
  currentConceptId?: string;
}

/**
 * Inner layout component that consumes the context.
 */
function DesktopLayoutInner({
  children,
  projectId,
}: ProjectDesktopLayoutProps) {
  const { isLg, isXl } = useBreakpoint();
  const layoutContext = useOptionalDesktopLayout();

  if (!isLg) {
    return <>{children}</>;
  }

  const renderSettingsData = layoutContext?.renderSettingsData;
  const showMiddlePanel = isXl && renderSettingsData;
  const setTopBarSlot = layoutContext?.setTopBarSlot;

  // <main> stays at slot 0 across renders; the inspector aside sits at
  // slot 1 as a conditional sibling (rendered or `false`). This keeps
  // main's child index stable when showMiddlePanel toggles — otherwise
  // ConceptEditor's mount-effects would re-toggle the structure and
  // loop ("Maximum update depth exceeded").
  return (
    <DesktopShell
      sidebar={<AppSidebar projectId={projectId} />}
    >
      <div className="flex-1 min-w-0 flex flex-col gap-4 h-full">
        {/* Full-width topbar slot. ConceptEditor (and any future
            two-column screen) portals its page header here so the bar
            stretches over content + inspector aside, matching Apple's
            macOS / iPad layout. `empty:hidden` collapses the slot when
            no consumer is publishing, so single-column screens render
            their header inline as before. */}
        <div
          ref={setTopBarSlot}
          // pt-4 aligns the bar with the sidebar card's `my-4` top edge — the
          // content column itself deliberately reaches the window top (see
          // DesktopShell), so the inset lives on the slot, not the shell.
          className="empty:hidden flex-shrink-0 pt-4"
        />
        <div className="flex-1 min-h-0 flex gap-4">
          {/* Content column — shares the page panel-gray background
              (the album cover / render card is the floating element).
              Apple HIG "inspector" convention places the settings panel
              on the trailing edge, so the content lives on the leading
              side. */}
          <main className="flex-1 min-w-0 overflow-y-auto">
            {children}
          </main>
          {showMiddlePanel && (
            // Inspector column has no chrome of its own — no fill, no
            // border, no shadow. SettingsCard groups (`bg-settings-card`)
            // float directly on the page panel-gray.
            <aside className="w-[350px] flex-shrink-0 h-full overflow-hidden flex flex-col mr-4">
              <UnifiedRenderPanel
                project={renderSettingsData.project}
                concept={renderSettingsData.concept}
                conceptAngles={renderSettingsData.conceptAngles}
                angles={renderSettingsData.angles}
                renders={renderSettingsData.renders}
                controls={renderSettingsData.controls}
                angleId={renderSettingsData.angleId}
                currentRenderId={renderSettingsData.currentRenderId}
                onRefetch={renderSettingsData.onRefetch}
              />
            </aside>
          )}
        </div>
      </div>
    </DesktopShell>
  );
}

/**
 * Desktop layout wrapper for project and concept pages.
 *
 * Breakpoint behavior:
 * - < lg (1024px): Mobile layout, renders children directly
 * - lg-xl (1024-1280px): 2-column layout (sidebar + content)
 * - >= xl (1280px): 3-pane layout (sidebar + content + inspector settings)
 */
export function ProjectDesktopLayout({
  children,
  projectId,
  currentConceptId,
}: ProjectDesktopLayoutProps) {
  return (
    <DesktopLayoutProvider>
      <DesktopLayoutInner
        projectId={projectId}
        currentConceptId={currentConceptId}
      >
        {children}
      </DesktopLayoutInner>
    </DesktopLayoutProvider>
  );
}
